// tslint:disable:no-non-null-assertion no-conditional-assignment
import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import {
  ExtendedHierarchyNode,
  HierarchyNodeViewModel,
  HierarchyService,
  HomeService,
  NodeId,
  NodeVM,
  OrganizationId,
  PublicationNode,
  SchemaLevel,
} from "src/core/backend/controllers";
import { Cacheable } from "ts-cacheable";

import { cacheClear$ } from "./cache-clear.service";

export const ROOT_NODE_ID = "root" as NodeId;

export type OrgsAndDocType = { name: string; docTypes: ExtendedHierarchyNode[] };

@Injectable({ providedIn: "root" })
export class HierarchyCacheService {
  private readonly hierarchyService = inject(HierarchyService);
  private readonly homeService = inject(HomeService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _nodeCache = new Map<string, ExtendedHierarchyNode>();
  private _rootNode: ExtendedHierarchyNode;

  constructor() {
    cacheClear$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this._nodeCache.clear());
  }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getNavigationRoot() {
    return this.homeService.getNavigationRoot().pipe(map((root) => this.initRoot(root)));
  }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getFirstNavNodeByName(name: SchemaLevel): Observable<ExtendedHierarchyNode | null> {
    return this.getNavigationRoot().pipe(map((root) => findNodeByName(root, name)));
  }

  getNavNodeById(id: NodeId): Observable<ExtendedHierarchyNode | null> {
    return this.getNavigationRoot().pipe(map((root) => findNodeById(root, id)));
  }

  // @Cacheable({ cacheBusterObserver: cacheClear$ })
  // getDocTypeIds(organization: OrganizationId | undefined | null): Observable<PublicationType[]> {
  //   return this.getDocTypes(organization).pipe(map((docTypes) => docTypes.map((p) => p.id)));
  // }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getPublicationNodes(): Observable<PublicationNode[]> {
    return this.getNavigationRoot().pipe(map((root) => Array.from(findNodes(root, isPublication))));
  }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getNavigationNode(nodeId: NodeId): Observable<ExtendedHierarchyNode | null> {
    return this.getNavigationRoot().pipe(map((root) => findNodeById(root, nodeId)));
  }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getOrgsAndDocTypes(): Observable<OrgsAndDocType[]> {
    return this.getNavigationRoot().pipe(
      map((nodes) =>
        findNodesByName(nodes, SchemaLevel.Organization).map(
          (orgNode): OrgsAndDocType => ({
            name: orgNode.title ?? "Unbetitelt",
            docTypes: findNodesByName(orgNode, SchemaLevel.Publication),
          })
        )
      )
    );
  }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getChildren(ofNode: string | null): Observable<ExtendedHierarchyNode[]> {
    const nodeId = ofNode || ROOT_NODE_ID;
    const cache = this._nodeCache.get(nodeId);

    return cache == null
      ? this.getNode(nodeId).pipe(map((node) => node?.children || []))
      : !cache.hasChildren
        ? of([])
        : cache.children != null
          ? of(cache.children)
          : this.hierarchyService.getNode(nodeId).pipe(map((node) => this.mergeChildrenWithCache(node, cache, [])));
  }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getNode(nodeId: string | null | undefined): Observable<ExtendedHierarchyNode | null> {
    const _nodeId = nodeId || ROOT_NODE_ID;
    const cache = this._nodeCache.get(_nodeId);

    return cache != null
      ? of(cache)
      : _nodeId === ROOT_NODE_ID
        ? this.hierarchyService.getNode(ROOT_NODE_ID).pipe(map((node) => this.initRoot(node)))
        : cache != null
          ? this.hierarchyService.getNode(_nodeId).pipe(map((node) => this.updateNodeAndChildrenInCache(node, cache)))
          : this.hierarchyService.getPathFromRoot(_nodeId, ROOT_NODE_ID, true).pipe(map((path) => this.mergePathWithCache(path)));
  }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getPath(nodeId: string | null): Observable<ExtendedHierarchyNode[]> {
    return this.getNode(nodeId).pipe(
      map(
        (node) =>
          node?.path.map((pathId) => {
            const cacheHit = this._nodeCache.get(pathId);
            if (cacheHit == null) {
              throw new Error(`Something went wrong with node caching! path:${node.path} missing:${pathId}`);
            }
            return cacheHit;
          }) || []
      )
    );
  }

  private initRoot(node: HierarchyNodeViewModel): ExtendedHierarchyNode {
    // this._rootNode = this.extendNode(node, null);
    // this._nodeCache.set(node.id, this._rootNode);
    // return this._rootNode;
    return this.initNode(node, null);
  }

  private initNode(node: HierarchyNodeViewModel, parent: ExtendedHierarchyNode | null) {
    const enode = this.extendNode(node, parent);
    enode.children = node.children?.map((n) => this.initNode(n, enode)) ?? null;
    this._nodeCache.set(node.id, enode);
    return enode;
  }

  private extendNode(node: HierarchyNodeViewModel, parentNode: ExtendedHierarchyNode | null) {
    const eNode: ExtendedHierarchyNode = {
      ...node,
      organization: NodeVM.isOrganization(node) ? (node.id as OrganizationId) : parentNode?.organization || null,
      path: parentNode?.path?.concat(node.id) ?? [node.id],
      children: null!,
    };
    eNode.children = this.mergeChildrenWithCache(node, eNode, null);
    return eNode;
  }

  private mergeChildrenWithCache(
    loadedNode: HierarchyNodeViewModel,
    existingNode: ExtendedHierarchyNode,
    defaultIfNone: ExtendedHierarchyNode[] | null
  ): ExtendedHierarchyNode[] {
    if (!loadedNode.hasChildren || !loadedNode.children || !loadedNode.children.length) {
      return defaultIfNone as any;
    }
    return (existingNode.children = loadedNode.children.map((child) => this.addNode(child, existingNode)));
  }

  private addNode(node: HierarchyNodeViewModel, parentNode: ExtendedHierarchyNode) {
    const existingNode = node && this._nodeCache.get(node.id);
    if (existingNode) {
      return existingNode;
    }
    const eNode = this.extendNode(node, parentNode);
    this._nodeCache.set(node.id, eNode);

    return eNode;
  }

  private mergePathWithCache(path: HierarchyNodeViewModel[]): ExtendedHierarchyNode {
    if (!Array.isArray(path)) {
      throw new Error(`Invalid path received ${JSON.stringify(path)}`);
    }
    let eNode: ExtendedHierarchyNode;
    let parentNode = this._rootNode;
    const nodes = path.slice();

    for (const node of nodes) {
      if (node == null) {
        console!.error("Null node in path:", path);
      }

      eNode = this.addNode(node, parentNode);
      if (node.children && node.children.length) {
        for (const child of node.children) {
          this.addNode(child, eNode);
        }
      }
      parentNode = eNode;
    }
    return eNode!;
  }

  private updateNodeAndChildrenInCache(node: HierarchyNodeViewModel, cached: ExtendedHierarchyNode) {
    cached.nodeName = node.nodeName;
    return cached;
  }
}

// Helper functions

function findNodes<T extends HierarchyNodeViewModel, R extends HierarchyNodeViewModel>(node: T, condition: (node: T | R) => node is R): R[];
function findNodes<T extends HierarchyNodeViewModel>(node: T, condition: (node: T) => boolean): T[];
function findNodes<T extends HierarchyNodeViewModel>(node: T, condition: (node: T) => boolean): T[] {
  return Array.from(_findNodes(node, condition));
}

function* _findNodes<T extends HierarchyNodeViewModel>(node: T, condition: (node: T) => boolean): Generator<T, void> {
  if (condition(node)) {
    yield node;
  } else if (node.children) {
    for (const n of node.children) {
      yield* _findNodes(n as T, condition);
    }
  }
}

function _findNode(node: ExtendedHierarchyNode, condition: (node: ExtendedHierarchyNode) => boolean): ExtendedHierarchyNode | null {
  if (condition(node)) {
    return node;
  } else if (node.children) {
    for (const n of node.children) {
      const found = _findNode(n, condition);
      if (found) return found;
    }
  }
  return null;
}

function findNodeByName(node: ExtendedHierarchyNode, nodeName: SchemaLevel): ExtendedHierarchyNode | null {
  return _findNode(node, (n) => n.nodeName === nodeName);
}

function findNodesByName(node: ExtendedHierarchyNode, nodeName: SchemaLevel): ExtendedHierarchyNode[] {
  return Array.from(_findNodes(node, (n) => n.nodeName === nodeName));
}

function findNodeById(node: ExtendedHierarchyNode, nodeId: NodeId): ExtendedHierarchyNode | null {
  return _findNode(node, (n) => n.id === nodeId);
}

function isPublication(node: ExtendedHierarchyNode | PublicationNode): node is PublicationNode & ExtendedHierarchyNode {
  return node.nodeName === SchemaLevel.Publication;
}
